/**
 * @fileoverview Synchronous priority-chain resolver for the initial
 * i18n language at boot time.
 *
 * Called by `createAppI18n` during factory setup. Returns a SUPPORTED
 * BCP-47 short tag, never undefined. Every step that yields an
 * unsupported code falls through to the next rather than letting the
 * UI render as i18next key paths.
 *
 * Priority:
 *   0. SSR locale (`ssrLocale`) — the authoritative locale of a
 *      server-prerendered page (BLOG-STD-001 full-shell SSR). When the
 *      document was rendered for a specific locale (e.g. `/es/blog/...`),
 *      the client MUST boot i18n to that same locale so the hydrated
 *      chrome matches the server markup. Present only on prerendered
 *      blog documents; absent for every normal app boot.
 *   1. JWT `language` claim (inline base64 decode of payload; NO
 *      cryptographic verification — auth-token-storage already vets
 *      the token before storing it)
 *   2. Storage cache via the canonical key STORAGE_KEY
 *   3. navigator.language short tag (region variants collapsed:
 *      `es-MX` → `es`)
 *   4. DEFAULT_LANGUAGE
 *
 * @module @puffin-core/i18n-bootstrap/getInitialLanguage
 */

import { decodeJwtPayload } from '@puffin-core/auth-token-storage/jwt';

import { DEFAULT_LANGUAGE, STORAGE_KEY, SUPPORTED_LANGUAGE_CODES } from './constants.js';

/**
 * Decode the payload segment of a JWT and return its `language` claim
 * (or null if undecodable / missing / non-string).
 *
 * Uses the canonical fail-closed `decodeJwtPayload` (HARD-JWTC-001) — a
 * DECODE-ONLY helper (no signature verification; auth-token-storage vets the
 * token before storing it). Imported from the pure `./jwt` subpath so this
 * dependency-free decoder never drags the browser-only storage layer into
 * the React-Native bundle (palm-native consumes this module).
 *
 * @param {string} token
 * @returns {string|null}
 */
function readLanguageClaim(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.language !== 'string') {
    return null;
  }
  return payload.language;
}

/**
 * Extract the short BCP-47 tag from navigator.language (or compatible).
 * `'es-MX'` → `'es'`, `'pt-BR'` → `'pt'`, etc.
 *
 * @param {*} navigatorObj
 * @returns {string|null}
 */
function readNavigatorShortTag(navigatorObj) {
  if (!navigatorObj || typeof navigatorObj.language !== 'string') {
    return null;
  }
  const tag = navigatorObj.language.toLowerCase().split('-')[0];
  return tag || null;
}

/**
 * Read the cached language from storage (if any).
 *
 * @param {{get: (key: string) => string|null}|null|undefined} storage
 * @returns {string|null}
 */
function readStorageCache(storage) {
  if (!storage || typeof storage.get !== 'function') {
    return null;
  }
  const value = storage.get(STORAGE_KEY);
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Resolve the initial language at boot time.
 *
 * @param {Object} [options]
 * @param {string|null} [options.ssrLocale] - Authoritative locale of a
 *   server-prerendered page (BLOG-STD-001). Highest priority when it is a
 *   supported code; ignored otherwise.
 * @param {string|null} [options.jwtToken] - Raw access token (the
 *   auth-token-storage default key). The function inline-decodes
 *   the payload to read `language`; never verifies signatures.
 * @param {{get: (key: string) => string|null}|null} [options.storage]
 *   - Storage adapter (web/native/memory).
 * @param {{language: string}|null} [options.navigator] - navigator
 *   object (DI'd for tests; default would be globalThis.navigator).
 * @param {ReadonlyArray<string>} [options.supportedCodes] - Allow-list.
 *   Defaults to SUPPORTED_LANGUAGE_CODES.
 * @returns {string} A supported BCP-47 short tag (always non-empty).
 */
export function getInitialLanguage({
  ssrLocale = null,
  jwtToken = null,
  storage = null,
  navigator = null,
  supportedCodes = SUPPORTED_LANGUAGE_CODES,
} = {}) {
  const supportedSet = new Set(supportedCodes);

  // Priority 0 — a server-prerendered page dictates the boot locale so the
  // hydrated chrome matches the server markup.
  if (typeof ssrLocale === 'string' && supportedSet.has(ssrLocale)) {
    return ssrLocale;
  }

  const fromJwt = readLanguageClaim(jwtToken);
  if (fromJwt && supportedSet.has(fromJwt)) {
    return fromJwt;
  }

  const fromStorage = readStorageCache(storage);
  if (fromStorage && supportedSet.has(fromStorage)) {
    return fromStorage;
  }

  const fromNavigator = readNavigatorShortTag(navigator);
  if (fromNavigator && supportedSet.has(fromNavigator)) {
    return fromNavigator;
  }

  return DEFAULT_LANGUAGE;
}
